import { Link } from '@tanstack/react-router'
import { CalendarPlus, CheckCircle2, LockKeyhole } from 'lucide-react'
import { useState } from 'react'

import type { OpenedBusinessDay } from '@/api/ledger-types'
import type { Branch } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { branchLabel } from '@/lib/branch'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'

import { BranchSelect } from './branch-reach'
import { formatMonthOf, today } from './constants'
import { useOpenBusinessDay } from './mutations'
import { LEDGER_PERMISSIONS } from './permissions'

export interface OpenBusinessDayCardProps {
  branches: Branch[]
  /** The branch the card opens on, usually the one the header is set to. */
  defaultBranchId: string | null
  canOpen: boolean
}

/**
 * Opening a branch's trading day.
 *
 * Nothing in the institution posts without one: every route into the ledger passes through the
 * posting service, and it refuses with `ledger.business_day_not_opened` when the branch has none.
 * This is the branch manager unlocking the door at eight in the morning — it authorises nothing
 * and moves nothing, which is why it is not countersigned and why it executes on the click.
 *
 * Two facts are stated before the click rather than after it. Opening the *first* day of a month
 * also creates that month's accounting period. And while a day opened here *can* now be closed here
 * — see `close-business-day.tsx` — nothing reopens a closed one, so an incorrect date is a mistake
 * that has to be worked around rather than undone.
 *
 * **Width policy.** The form refuses below `lg` while the position above it does not. That split
 * is deliberate and follows `docs/mobile-policy.md`: reading whether a branch is trading is
 * exactly the kind of question somebody has from a phone, and choosing the date every posting at
 * that branch will land on — with no way to undo it — is not.
 */
export function OpenBusinessDayCard({ branches, defaultBranchId, canOpen }: OpenBusinessDayCardProps) {
  // The operator's own choice, and nothing until they make one. The default is applied below
  // rather than seeded into state: `defaultBranchId` is null on the first render — the branch list
  // has not arrived and the header may not have settled its own selection yet — and lazily
  // initialised state would keep that first null for the life of the card, leaving the field
  // stubbornly empty for exactly the branch manager it is meant to pre-fill.
  const [chosenBranchId, setChosenBranchId] = useState<string | null>(null)
  const [businessDate, setBusinessDate] = useState(today)
  const [opened, setOpened] = useState<OpenedBusinessDay | null>(null)

  const open = useOpenBusinessDay()

  if (!canOpen) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockKeyhole className="size-4 text-muted" aria-hidden />
            Opening a day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-body">
            Opening a trading day needs{' '}
            <span className="font-mono text-xs">{LEDGER_PERMISSIONS.dayOpen}</span>. Your role does
            not hold it, so the position above is read-only for you — which is the ordinary
            arrangement: the branch manager opens the day.
          </p>
        </CardContent>
      </Card>
    )
  }

  const branchId = chosenBranchId ?? defaultBranchId
  const selected = branches.find((branch) => branch.id === branchId) ?? null

  /*
   * The branch has to be one the server confirmed, not merely an identifier this client is
   * holding. The default arrives from session storage, which can carry a branch left behind by an
   * earlier session or one an access review has since revoked — the header corrects that a moment
   * later, but not before this card has rendered. Submitting on the raw identifier would open a
   * trading day at a branch that is named nowhere on this screen, and nothing in this application
   * can close a day again.
   */
  const submittable = selected !== null && businessDate !== ''

  return (
    <RequiresWiderScreen
      screen="Opening a business day"
      reason="It fixes the date every posting at that branch will carry, the first day of a month creates the accounting period with it, and nothing in this application can close a day again."
    >
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <CalendarPlus className="size-4 text-brand-text" aria-hidden />
              Open a business day
            </CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Not countersigned — this takes effect immediately.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <BranchSelect
              id="open-day-branch"
              label="Branch"
              branches={branches}
              value={selected?.id ?? null}
              onChange={(next) => {
                setChosenBranchId(next)
                setOpened(null)
                open.reset()
              }}
              description="Only branches the server has confirmed you may act in are offered."
            />

            <div className="space-y-1.5">
              <Label htmlFor="open-day-date">Business date</Label>
              <Input
                id="open-day-date"
                type="date"
                value={businessDate}
                max={today()}
                onChange={(event) => {
                  setBusinessDate(event.target.value)
                  setOpened(null)
                  open.reset()
                }}
                aria-describedby="open-day-date-help"
              />
              <p id="open-day-date-help" className="text-xs text-muted">
                Today by default. A past date is how a branch that did not trade on Monday still
                opens Monday, so that Monday&apos;s collections post into it. A future date is
                refused.
              </p>
            </div>
          </div>

          <Alert tone="warning">
            <LockKeyhole aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Nothing reopens a day once it is closed</AlertTitle>
              <AlertDescription>
                Closing needs{' '}
                <span className="font-mono text-xs">{LEDGER_PERMISSIONS.dayClose}</span> and is done
                under &ldquo;Close the day&rdquo;. There is no endpoint that reopens a closed day, so
                a day opened on the wrong date has to be closed again rather than undone — and every
                posting made into it stays. Check the branch and the date before confirming.
              </AlertDescription>
            </div>
          </Alert>

          {open.isError ? (
            <div className="space-y-2">
              <ProblemAlert error={open.error} />
              {open.error.code === 'ledger.previous_day_still_open' ? (
                // The server has named the day in the way; this is the door to it. Days are
                // sequential per branch, and the earlier one closes under "Close the day".
                <Button asChild variant="secondary" size="sm">
                  <Link to="/ledger/business-days">Go to the day register to close it</Link>
                </Button>
              ) : null}
            </div>
          ) : null}

          {opened ? (
            <Alert tone="success">
              <CheckCircle2 aria-hidden />
              <div className="space-y-1">
                <AlertTitle>
                  {branchLabel(branches.find((branch) => branch.id === opened.branchId))} is trading
                  on {formatBusinessDate(opened.businessDate)}
                </AlertTitle>
                <AlertDescription>
                  Opened at{' '}
                  <span className="numeric tabular-nums">{formatDateTime(opened.openedAtUtc)}</span>.
                  If the day was already open, this is the same answer — opening is deliberately
                  repeatable, and the time shown is when it was first opened rather than now.
                  {opened.periodCreated ? (
                    <>
                      {' '}
                      This was the first day opened in {formatMonthOf(opened.businessDate)}, so the
                      accounting period for that month was created with it. It is now a period
                      somebody has to close at month end.
                    </>
                  ) : null}
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={!submittable || open.isPending}
              onClick={() => {
                if (selected === null) {
                  return
                }

                open.mutate(
                  // The identifier is a string throughout this application and an int64 on the
                  // endpoint, so it converts here, at the edge, and nowhere else. It is the
                  // selected branch's own identifier — never a value this screen has not shown.
                  { branchId: Number(selected.id), businessDate },
                  { onSuccess: setOpened },
                )
              }}
            >
              {open.isPending ? 'Opening…' : 'Open the day'}
            </Button>
            {selected ? (
              <p className="text-xs text-muted">
                {branchLabel(selected)} · {businessDate === '' ? 'no date' : formatBusinessDate(businessDate)}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </RequiresWiderScreen>
  )
}
